/**
 * Bends in a cabinet run — the pure maths every bend rule reads.
 *
 * A run is a polyline of legs: wall-backed legs (`{ wall, startParam,
 * endParam }`) and free-standing legs — stored as `{ freestanding, start, end }`
 * or, inside a fill, as a synthesized virtual wall marked `drawnFreeLeg`. A
 * BEND is the joint between two consecutive legs. This module answers, for
 * either shape: which way a leg travels, where a bend is, how far it turns,
 * and whether it is too sharp to fill.
 *
 * The two numbers are the user's (design 2026-11-03,
 * claude-docs/2026-11-03-angled-cabinet-runs-design.md): a new leg snaps every
 * 45° within `BEND_SNAP_DEG`, and a bend that involves a free leg may turn at
 * most `MAX_FREE_BEND_DEG` — sharper is drawn as two runs. Two WALL legs are
 * never refused: the room dictates their angle, and the wall-corner fill
 * handles any of them.
 *
 * Pure. No I/O. Must not require `corners.js` (which requires this).
 */

const {
  getWallAxes,
  wallParamToSurfacePoint,
} = require('../kitchen-assembly/wallMath');

const BEND_SNAP_DEG = 5;
const MAX_FREE_BEND_DEG = 90;
// Two legs whose ends are within this are joined — the engine's shared-vertex
// tolerance (`corners.js` reads it from here).
const JOIN_EPS = 1e-3;
// The ONE allowance on the angle window, so every reader of "is this a bend,
// too sharp, or straight on" agrees at the edges: the corner plan, the
// combined fill's router and the draw tool read the same turn by different
// arithmetic, and at exactly 5° one read 5.0000 and another 4.9999
// (whole-branch review, 2026-11-03).
const TURN_EPS_DEG = 1e-3;

/**
 * A turn a bend through a free leg is planned for: OVER 5°, up to 90°. A turn
 * of 5° or less is the snap's own catch — straight on — and the corner stop
 * (`cornerDetection.runStopDistance`) stops nothing there either, so 5° itself
 * belongs to "straight" for every reader.
 */
const isFillableTurn = (deg) =>
  Math.abs(deg) > BEND_SNAP_DEG + TURN_EPS_DEG &&
  Math.abs(deg) <= MAX_FREE_BEND_DEG + TURN_EPS_DEG;
/** Sharper than a bend through a free leg may turn. */
const isTooSharpTurn = (deg) =>
  Math.abs(deg) > MAX_FREE_BEND_DEG + TURN_EPS_DEG;
/** Within the draw tool's snap: one straight leg drawn with an extra click. */
const isStraightOnTurn = (deg) => Math.abs(deg) <= BEND_SNAP_DEG + TURN_EPS_DEG;
/**
 * A right angle, exactly — what a bend through a DRAWN leg must be to take a
 * square corner's cabinet. The draw tool snaps a leg to 90° from the one before
 * it, so a drawn right angle is exact; anything else is the angle the user
 * drew, and an L built for 90° in an 87° bend stood out from under the counter
 * (the user, 2026-11-03: "a corner 90 is incorrect for a 87 deg bend right?").
 * A corner between two WALLS keeps the generator's 5° (`isSquareCornerCos`):
 * real walls are rarely square, and their cabinets are set square and scribed.
 */
const isRightTurn = (deg) => Math.abs(Math.abs(deg) - 90) <= TURN_EPS_DEG;

const unit = (x, z) => {
  const len = Math.hypot(x, z);
  return len < 1e-12 ? null : { x: x / len, z: z / len };
};

/** A stored free leg, or a drawn free leg synthesized as a virtual wall. */
function isFreeLeg(seg) {
  return seg?.freestanding === true || seg?.wall?.drawnFreeLeg === true;
}

/** The leg's two ends in world space, in its direction of travel. */
function legEndpoints(seg) {
  if (!seg) return null;
  if (seg.freestanding) {
    return seg.start && seg.end ? { start: seg.start, end: seg.end } : null;
  }
  if (!seg.wall) return null;
  const start = wallParamToSurfacePoint(seg.wall, seg.startParam);
  const end = wallParamToSurfacePoint(seg.wall, seg.endParam);
  return start && end ? { start, end } : null;
}

/** Unit direction of travel, start → end; null for a degenerate leg. */
function legDirection(seg) {
  if (seg && !seg.freestanding && seg.wall) {
    const axes = getWallAxes(seg.wall);
    const sign = Math.sign(seg.endParam - seg.startParam);
    if (!axes || !sign) return null;
    return { x: axes.wallDir.x * sign, z: axes.wallDir.z * sign };
  }
  const ends = legEndpoints(seg);
  return ends
    ? unit(ends.end.x - ends.start.x, ends.end.z - ends.start.z)
    : null;
}

/**
 * Signed turn from `dirIn` to `dirOut`, in degrees, in (-180, 180]. Positive
 * turns toward `leftRot(dirIn) = (-z, x)` — the engine's inward-normal side.
 */
function turnDeg(dirIn, dirOut) {
  const cross = dirIn.x * dirOut.z - dirIn.z * dirOut.x;
  const dot = dirIn.x * dirOut.x + dirIn.z * dirOut.z;
  return (Math.atan2(cross, dot) * 180) / Math.PI;
}

/**
 * The bend between two consecutive legs: where it is, how far it turns, and
 * whether the legs actually meet there. Null when either leg has no
 * direction.
 */
function bendAt(segA, segB) {
  const a = legEndpoints(segA);
  const b = legEndpoints(segB);
  const dA = legDirection(segA);
  const dB = legDirection(segB);
  if (!a || !b || !dA || !dB) return null;
  const gap = Math.hypot(a.end.x - b.start.x, a.end.z - b.start.z);
  return {
    vertex: { x: a.end.x, z: a.end.z },
    turnDeg: turnDeg(dA, dB),
    joined: gap <= JOIN_EPS,
    free: isFreeLeg(segA) || isFreeLeg(segB),
  };
}

/** Joined bends involving a free leg that turn more than the limit. */
function tooSharpBends(segments) {
  const out = [];
  for (let i = 0; i < (segments || []).length - 1; i += 1) {
    const b = bendAt(segments[i], segments[i + 1]);
    if (!b || !b.joined || !b.free) continue;
    if (isTooSharpTurn(b.turnDeg)) {
      out.push({ index: i, turnDeg: b.turnDeg });
    }
  }
  return out;
}

/**
 * Which side of its own travel a wall-backed leg's cabinets stand on: +1 on
 * `leftRot(travel)` (its wall's inward normal), −1 the other. Null for a leg
 * with no wall (a stored free leg — its side is the run's, `runSide.js`).
 */
function cabinetSideSign(seg) {
  if (!seg?.wall) return null;
  const n = getWallAxes(seg.wall)?.wallNormal;
  const d = legDirection(seg);
  if (!n || !d) return null;
  return n.x * -d.z + n.z * d.x >= 0 ? 1 : -1;
}

/**
 * Do two wall-backed legs keep their cabinets on the same side of the drawn
 * line? A run carries one side, so its island legs always do; only a wall leg
 * whose room side is the OTHER side of the line breaks it — the cabinets
 * switch sides there, which is no corner at all (whole-branch review).
 */
function sameCabinetSide(segA, segB) {
  const a = cabinetSideSign(segA);
  const b = cabinetSideSign(segB);
  return a == null || b == null || a === b;
}

/** Length of a leg, start to end; 0 for one that cannot be resolved. */
function legLength(seg) {
  const ends = legEndpoints(seg);
  return ends
    ? Math.hypot(ends.end.x - ends.start.x, ends.end.z - ends.start.z)
    : 0;
}

/** Intersection of the lines `p + t·d` and `q + s·e`, or null when parallel. */
function lineCrossing(p, d, q, e) {
  const den = d.x * e.z - d.z * e.x;
  if (Math.abs(den) < 1e-12) return null;
  const t = ((q.x - p.x) * e.z - (q.z - p.z) * e.x) / den;
  return { x: p.x + d.x * t, z: p.z + d.z * t };
}

module.exports = {
  BEND_SNAP_DEG,
  MAX_FREE_BEND_DEG,
  JOIN_EPS,
  isFillableTurn,
  isTooSharpTurn,
  isStraightOnTurn,
  isRightTurn,
  cabinetSideSign,
  sameCabinetSide,
  isFreeLeg,
  legEndpoints,
  legDirection,
  turnDeg,
  bendAt,
  tooSharpBends,
  legLength,
  lineCrossing,
};
